import { createContext, PropsWithChildren, useContext, useState } from 'react'
import { User, WorkShift } from 'typings/common_defs'

export type AssignmentWorkflowsContextState = {
  requestWorkersFlow: {
    open: (selectedShifts: string[]) => void
    close: () => void
    isOpen: boolean
    selectedShifts?: string[]
  }
  editShiftFlow: {
    open: (selectedShifts: string[]) => void
    close: () => void
    isOpen: boolean
    selectedShifts?: string[]
  }
  cancelShiftFlow: {
    open: (selectedShifts: string[]) => void
    close: () => void
    isOpen: boolean
    selectedShifts?: string[]
  }
  removeWorkerFlow: {
    open: (worker?: User, shift?: WorkShift) => void
    close: () => void
    isOpen: boolean
    worker?: User
    shift?: WorkShift
  }
}

const AssignmentWorkflowsContext =
  createContext<AssignmentWorkflowsContextState | null>(null)

export const useAssignmentWorkflowsContextStateValue = () => {
  const context = useContext(AssignmentWorkflowsContext)
  if (!context) {
    throw new Error(
      'useAssignmentWorkflowsContextStateValue must be used within a AssignmentWorkflowsProvider'
    )
  }
  return context
}

type AssignmentWorkflowsProviderProps = PropsWithChildren

/**
 * Provides the state for the assignment workflows such as the request workers, etc.
 */
export const AssignmentWorkflowsProvider = ({
  children,
}: AssignmentWorkflowsProviderProps) => {
  const [isRequestWorkersOpen, setIsRequestWorkersOpen] = useState(false)
  const [isEditShiftOpen, setIsEditShiftOpen] = useState(false)
  const [isRemoveWorkerOpen, setIsRemoveWorkerOpen] = useState(false)
  const [selectedShifts, setSelectedShifts] = useState<string[]>([])
  const [worker, setWorker] = useState<User>()
  const [shift, setShift] = useState<WorkShift>()
  const [isCancelFlowOpen, setIsCancelFlowOpen] = useState(false)
  const value: AssignmentWorkflowsContextState = {
    requestWorkersFlow: {
      open: (shifts: string[]) => {
        setSelectedShifts(shifts)
        setIsRequestWorkersOpen(true)
      },
      close: () => setIsRequestWorkersOpen(false),
      isOpen: isRequestWorkersOpen,
      selectedShifts,
    },
    editShiftFlow: {
      open: (shifts: string[]) => {
        setSelectedShifts(shifts)
        setIsEditShiftOpen(true)
      },
      close: () => {
        setIsEditShiftOpen(false)
        setSelectedShifts([])
      },
      isOpen: isEditShiftOpen,
      selectedShifts,
    },
    cancelShiftFlow: {
      open: (selectedShifts: string[]) => {
        setSelectedShifts(selectedShifts)
        setIsCancelFlowOpen(true)
      },
      close: () => setIsCancelFlowOpen(false),
      isOpen: isCancelFlowOpen,
      selectedShifts,
    },
    removeWorkerFlow: {
      open: (worker?: User, shift?: WorkShift) => {
        if (worker) {
          setWorker(worker)
        }
        if (shift) {
          setShift(shift)
        }
        setIsRemoveWorkerOpen(true)
      },
      close: () => {
        setIsRemoveWorkerOpen(false)
      },
      isOpen: isRemoveWorkerOpen,
      worker,
      shift,
    },
  }

  return (
    <AssignmentWorkflowsContext.Provider value={value}>
      {children}
    </AssignmentWorkflowsContext.Provider>
  )
}
